import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleGenerationRow } from '../../types/articleGeneration';
import { buildMarkdown } from '../ArticleGeneration/markdown';

interface Props {
  runs: ArticleGenerationRow[];
  domains: { id: string; domain: string }[];
  onOpen: (run: ArticleGenerationRow) => void;
}

export const runTitle = (run: ArticleGenerationRow): string =>
  run.structure?.h1 || run.briefs?.h1 || run.inputs.primaryKeyword;

export const runWordCount = (run: ArticleGenerationRow): number | null => {
  if (run.mode !== 'full_article' || !run.sections || !run.structure) return null;
  return buildMarkdown(run.structure.h1, run.sections).trim().split(/\s+/).filter(Boolean).length;
};

// A run left in 'generating' with no update for this long was interrupted
// (tab closed mid-stream) and is effectively incomplete. Display-only — the
// DB row is not mutated, so a genuinely running generation is never mislabelled.
const STALE_GENERATING_MS = 10 * 60 * 1000;
export const effectiveStatus = (run: ArticleGenerationRow): string =>
  run.status === 'generating' &&
  Date.now() - new Date(run.updated_at).getTime() > STALE_GENERATING_MS
    ? 'incomplete'
    : run.status;

const MODES = ['full_article', 'headings', 'headings_briefs'] as const;
const STATUSES = ['generating', 'needs_review', 'done', 'incomplete', 'cancelled', 'error'] as const;

const RunList: React.FC<Props> = ({ runs, domains, onOpen }) => {
  const { t } = useTranslation();
  const [domainFilter, setDomainFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      runs.filter(
        (run) =>
          (!domainFilter || run.domain_id === domainFilter) &&
          (!modeFilter || run.mode === modeFilter) &&
          (!statusFilter || effectiveStatus(run) === statusFilter) &&
          (!search ||
            runTitle(run).toLowerCase().includes(search.toLowerCase()) ||
            run.inputs.primaryKeyword.toLowerCase().includes(search.toLowerCase()))
      ),
    [runs, domainFilter, modeFilter, statusFilter, search]
  );

  return (
    <div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} className="p-2 border rounded-md text-sm">
          <option value="">{t('article.history_filter_domain')}: {t('article.history_filter_all')}</option>
          {domains.map((d) => (
            <option key={d.id} value={d.id}>{d.domain}</option>
          ))}
        </select>
        <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="p-2 border rounded-md text-sm">
          <option value="">{t('article.history_filter_mode')}: {t('article.history_filter_all')}</option>
          {MODES.map((m) => (
            <option key={m} value={m}>{t(`article.mode_${m}`)}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="p-2 border rounded-md text-sm">
          <option value="">{t('article.history_filter_status')}: {t('article.history_filter_all')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`article.version_${s}`)}</option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('article.history_search')}
          className="flex-grow min-w-[12rem] p-2 border rounded-md text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-gray-500 text-sm">{t('article.history_empty')}</div>
      ) : (
        <table className="w-full text-sm border rounded-md">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b">
              <th className="p-2">{t('article.history_col_date')}</th>
              <th className="p-2">{t('article.history_col_title')}</th>
              <th className="p-2">{t('article.history_col_mode')}</th>
              <th className="p-2">{t('article.history_col_words')}</th>
              <th className="p-2">{t('article.history_col_status')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((run) => (
              <tr
                key={run.id}
                onClick={() => onOpen(run)}
                className="border-b last:border-0 hover:bg-blue-50 cursor-pointer"
              >
                <td className="p-2 whitespace-nowrap">{new Date(run.created_at).toLocaleDateString('en-GB')}</td>
                <td className="p-2">{runTitle(run)}</td>
                <td className="p-2">{t(`article.mode_${run.mode}`)}</td>
                <td className="p-2 font-mono text-xs">{runWordCount(run) ?? '–'}</td>
                <td className="p-2">{t(`article.version_${effectiveStatus(run)}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RunList;
